import { Card } from "react-bootstrap";
import NewDynamicDate from "./FormComponents/NewDynamicDate";
import NewDynamicText from "./FormComponents/NewDynamicText";
import NewDynamicSelect from "./FormComponents/NewDynmaicSelect";
import { useEffect, useState } from "react";
import Icon from "../generic/Icon";
import { truncateText, formatNumber } from "./utils";
import { useSiteContext } from "./Providers/SSCProvider";
import DiverseIcon from "./DiverseIcon";
import { useStructureContext } from "./Providers/StructureProvider";

const SingleCircuit = ({
  initialCircuit,
  circuitFields,
  invalidFields,
  setShowDiverseModal,
  setNotesModal,
  setCicruitModalData,
}) => {
  const [updatedCircuit, setUpdatedCircuit] = useState(initialCircuit);

  const { handleCircuitChange, selectedSite, removeTempCircuit } =
    useSiteContext();
  const { highlightData } = useStructureContext();

  useEffect(() => {
    setUpdatedCircuit(initialCircuit);
  }, [initialCircuit]);

  const valueChangeHandler = ({ key, value }) => {
    const newCircuit = { ...updatedCircuit, [key]: value };
    setUpdatedCircuit(newCircuit);

    const diff = {};
    Object.keys(newCircuit).forEach((k) => {
      if (newCircuit[k] !== initialCircuit[k]) diff[k] = newCircuit[k];
    });

    handleCircuitChange(
      initialCircuit.structureId,
      initialCircuit.circuitGmId,
      diff,
    );
  };

  const circuitGroups =
    selectedSite?.diversityGroups?.filter((group) =>
      updatedCircuit.diversityGroupIds?.includes(group.id),
    ) || [];

  const isHighlighted =
    highlightData &&
    initialCircuit.diversityGroupIds?.includes(highlightData.groupId);

  const renderField = (field) => {
    const isUpdatedValue =
      updatedCircuit[field.key] !== initialCircuit[field.key];

    // Diversity field
    if (field.type === "diversity") {
      return (
        <div
          key={field.key}
          className="flex gap-2 overflow-x-auto whitespace-nowrap p-1"
          style={{ scrollbarWidth: "thin", minHeight: "40px" }}
        >
          {circuitGroups.map((group) => (
            <DiverseIcon
              key={group.id}
              group={group}
              count={group.circuitCount}
              variant={group.diversity}
              disabled={false}
              setShowDiverseModal={setShowDiverseModal}
              value={group.diversityStatus?.id}
            />
          ))}
        </div>
      );
    }

    // Select field
    if (field.type === "select") {
      return (
        <div
          key={field.key}
          style={{ display: "flex", alignItems: "center", gap: 4 }}
        >
          <NewDynamicSelect
            valueKey={field.key}
            value={updatedCircuit[field.key] ?? ""}
            options={field.options}
            getOptionLabel={field.getOptionLabel}
            getOptionValue={field.getOptionValue}
            placeholder={field.placeholder}
            isUpdated={isUpdatedValue}
            isRequired={invalidFields.includes(field.key)}
            onChange={valueChangeHandler}
            inputWidth={field.width}
          />

          {(field.key === "carrier" || field.key === "lec") && (
            <InfoIcon
              carrierName={
                field.key === "lec"
                  ? updatedCircuit.lec
                  : updatedCircuit.carrier
              }
            />
          )}
        </div>
      );
    }

    // Date field
    if (field.type === "date") {
      return (
        <NewDynamicDate
          key={field.key}
          valueKey={field.key}
          value={updatedCircuit[field.key] || ""}
          isUpdated={isUpdatedValue}
          onChange={valueChangeHandler}
          inputWidth={field.width}
        />
      );
    }

    // Text field
    return (
      <div
        key={field.key}
        style={{ display: "flex", alignItems: "center", gap: 4 }}
      >
        <NewDynamicText
          valueKey={field.key}
          value={
            field.key === "mrcBaseline"
              ? formatNumber(updatedCircuit[field.key])
              : updatedCircuit[field.key] || ""
          }
          isUpdated={isUpdatedValue}
          isRequired={invalidFields.includes(field.key)}
          onChange={valueChangeHandler}
          inputWidth={field.width}
        />

        {field.key === "circuitVendorId" && (
          <CircuitIcons
            updatedCircuit={updatedCircuit}
            initialCircuit={initialCircuit}
            setNotesModal={setNotesModal}
            setCicruitModalData={setCicruitModalData}
            removeTempCircuit={removeTempCircuit}
          />
        )}

        {field.width === 50 && (
          <label className="flex items-center space-x-1 text-black ps-1">
            <span className="text-sm">Kbs</span>
          </label>
        )}
      </div>
    );
  };

  return (
    <Card
      className="ps-2  pt-2 pe-0 pb-2"
      style={{
        border: "2px solid",
        borderColor: isHighlighted ? highlightData?.color : "transparent",
        transition: "border-color 0.6s ease",
        backgroundColor:
          initialCircuit.status === "Decom" ? "rgb(236, 236, 236)" : "white",
        width: "225px",
        flexShrink: 0,
      }}
    >
      <Card.Body className="ps-0 pe-1 pb-0">
        {circuitFields.map((field, idx) => {
          const next = circuitFields[idx + 1];
          const sectionEnds = !next || next.section !== field.section;

          // Inline group handling
          if (field.inlineGroup === "statusUse") {
            const statusUseFields = circuitFields.filter(
              (f) => f.inlineGroup === "statusUse",
            );
            if (field.key !== statusUseFields[0].key) return null;

            // Determine if the inline group is the last thing in the section
            const groupLastKey =
              statusUseFields[statusUseFields.length - 1].key;
            const groupLastIndex = circuitFields.findIndex(
              (f) => f.key === groupLastKey,
            );
            const afterGroup = circuitFields[groupLastIndex + 1];
            const groupSectionEnds =
              !afterGroup || afterGroup.section !== field.section;

            return (
              <div
                key="statusUse"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginBottom: groupSectionEnds ? "1.5rem" : 0, // ✅ gap AFTER section
                }}
              >
                {statusUseFields.map((f, i) => (
                  <div
                    key={f.key}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {renderField(f)}
                    {i === 0 && <span className="mx-2">/</span>}
                  </div>
                ))}
              </div>
            );
          }

          // Diversity
          if (field.type === "diversity") {
            return (
              <div
                key={field.key}
                style={{ marginBottom: sectionEnds ? "1.5rem" : 0 }} // ✅ gap AFTER section
              >
                {renderField(field)}
              </div>
            );
          }

          // Regular fields
          return (
            <div
              key={field.key}
              style={{ marginBottom: sectionEnds ? "1.5rem" : 0 }} // ✅ gap AFTER section
            >
              {renderField(field)}
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

const CircuitIcons = ({
  updatedCircuit,
  initialCircuit,
  setNotesModal,
  setCicruitModalData,
  removeTempCircuit,
}) => {
  const hasNote = !!updatedCircuit.circuitNote;

  return (
    <>
      <Icon
        name="delete"
        onClick={() => {
          if (initialCircuit.isNew) {
            removeTempCircuit(
              initialCircuit.structureId,
              initialCircuit.circuitGmId,
            );
          } else {
            setCicruitModalData({ show: true, circuit: initialCircuit });
          }
        }}
        overrides={{
          tooltip: "Delete Circuit",
          tooltipPlacement: "top",
          size: "1rem",
          style: { marginLeft: "5px" },
        }}
      />
      <Icon
        name="note"
        onClick={() =>
          setNotesModal({
            show: true,
            circuitNote: updatedCircuit.circuitNote,
            circuitId: updatedCircuit.circuitGmId,
            structureId: updatedCircuit.structureId,
          })
        }
        overrides={{
          tooltip: hasNote
            ? truncateText(
                updatedCircuit.circuitNote.replace(/\\n/g, "\n"),
                200,
              )
            : "No notes available",
          tooltipPlacement: "auto",
          tooltipClass: "pre-wrap-tooltip",
          iconColor: hasNote ? "#CA8A04" : "#808080",
          style: { marginLeft: "10px", cursor: "pointer" },
        }}
      />
    </>
  );
};

const InfoIcon = ({ carrierName }) => {
  const { handleCarrierInfoClick } = useSiteContext();
  return (
    <Icon
      name="info"
      onClick={() => handleCarrierInfoClick(carrierName || "")}
      overrides={{
        tooltip: "Carrier Info",
        tooltipPlacement: "auto",
        size: "1rem",
        style: { marginLeft: "10px" },
      }}
    />
  );
};

export default SingleCircuit;
